import { Button, Form, Input, InputNumber, Modal, Select, Table, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { ExclamationCircleOutlined } from '../../icons';
import { fieldError } from '../../lib/http';
import { isBookable, openStatusLabel, sourceLabel, verificationLabel } from '../../lib/status';
import { holidaysApi, labsApi, periodsApi, reservationsApi } from '../../services/resources';
import type { Holiday, Lab, Period, Reservation } from '../../types/api';

export function AdminLabsPage() {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [occ, setOcc] = useState<Reservation[]>([]);
  const [occError, setOccError] = useState('');
  const [open, setOpen] = useState(false);

  async function load() {
    const [l, p, h] = await Promise.all([labsApi.list({ page: 1, limit: 50 }), periodsApi.list(), holidaysApi.list()]);
    setLabs(l.items);
    setPeriods(p);
    setHolidays(h.items);
    try {
      const r = await reservationsApi.list({ page: 1, limit: 100 });
      setOcc(r.items.filter((x) => x.status === 'pending' || x.status === 'approved'));
      setOccError('');
    } catch {
      setOccError('占用数据失败。');
    }
  }

  useEffect(() => {
    void load().catch(() => message.error('实验室列表失败'));
  }, []);

  return (
    <div className="stack">
      <h1 className="page-title">实验室与节次</h1>
      <p className="meta">仅「开放且已核验」可被教师/学生约。未核验改开放会失败。</p>
      <Button type="primary" className="touch" onClick={() => setOpen(true)}>
        新增实验室
      </Button>
      <Table
        scroll={{ x: 620 }}
        rowKey="id"
        dataSource={labs}
        pagination={false}
        columns={[
          { title: '房间编码', render: (_, r) => <span className="mono">{r.room_code}</span> },
          { title: '名称', dataIndex: 'name' },
          { title: '开放', render: (_, r) => openStatusLabel(r.open_status) },
          { title: '核验', render: (_, r) => verificationLabel(r.verification_status) },
          { title: '容量', dataIndex: 'capacity' },
          {
            title: '操作',
            render: (_, r) => (
              <Button
                className="touch"
                onClick={async () => {
                  try {
                    if (r.verification_status !== 'verified') {
                      await labsApi.patch(r.id, { verification_status: 'verified' });
                    } else if (!isBookable(r)) {
                      await labsApi.patch(r.id, { open_status: 'open' });
                    }
                    await load();
                  } catch {
                    message.error('请先完成核验');
                  }
                }}
              >
                {r.verification_status !== 'verified' ? '核验' : '开放'}
              </Button>
            ),
          },
        ]}
      />
      <h2 className="page-title">节次目录</h2>
      <Table
        scroll={{ x: 620 }}
        rowKey="id"
        dataSource={periods}
        pagination={false}
        columns={[
          { title: '名称', dataIndex: 'name' },
          { title: '开始', dataIndex: 'starts_local' },
          { title: '结束', dataIndex: 'ends_local' },
        ]}
      />
      <h2 className="page-title">假期（SLA 跳过）</h2>
      <ul>
        {holidays.map((h) => (
          <li key={h.id}>
            {h.date} {h.name}
          </li>
        ))}
      </ul>
      <ConflictGrid labs={labs} periods={periods} occ={occ} error={occError} />
      <LabModal open={open} onClose={() => setOpen(false)} onOk={() => void load()} />
    </div>
  );
}

function ConflictGrid(props: { labs: Lab[]; periods: Period[]; occ: Reservation[]; error: string }) {
  const cells = useMemo(() => props.occ, [props.occ]);
  if (props.error) return <EmptyState text={props.error} />;
  if (cells.length === 0) return <EmptyState text="本周未排课。" />;
  return (
    <section className="surface" style={{ padding: 16, overflow: 'auto' }}>
      <h2 className="page-title">
        <ExclamationCircleOutlined /> 冲突日历
      </h2>
      <div className="stack">
        {cells.map((o) => (
          <div key={o.id} className="slot-cell" aria-disabled="true">
            {sourceLabel(o.source)} · {o.remark || o.user_name || o.id} · {o.date}{' '}
            {(o.period_names ?? []).join('、')}
          </div>
        ))}
      </div>
    </section>
  );
}

function LabModal(props: { open: boolean; onClose: () => void; onOk: () => void }) {
  const [room_code, setCode] = useState('');
  const [name, setName] = useState('');
  const [capacity, setCap] = useState(40);
  const [codeErr, setCodeErr] = useState<string>();
  return (
    <Modal
      title="新增实验室"
      open={props.open}
      onCancel={props.onClose}
      onOk={async () => {
        try {
          await labsApi.create({ name, room_code, capacity, open_status: 'closed' });
          props.onOk();
          props.onClose();
        } catch (e) {
          setCodeErr(fieldError(e, 'room_code') ?? '保存失败');
        }
      }}
    >
      <Form layout="vertical">
        <Form.Item label="房间编码" validateStatus={codeErr ? 'error' : undefined} help={codeErr}>
          <Input className="mono" value={room_code} onChange={(e) => setCode(e.target.value)} />
        </Form.Item>
        <Form.Item label="名称">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Form.Item>
        <Form.Item label="容量">
          <InputNumber min={1} value={capacity} onChange={(v) => setCap(v ?? 1)} />
        </Form.Item>
        <Form.Item label="开放状态">
          <Select disabled value="closed" options={[{ value: 'closed', label: '关闭（新建默认）' }]} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
